import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import LockerAssignModal from './LockerAssignModal';
import { Search, History, RefreshCw, X, Users, Eye, Unlock, Lock, Zap } from 'lucide-react';
import { formatDateTime } from '../utils/datetime';

const PAGE_SIZE = 20;

const displayNameOf = (registration) => {
  const fd = registration?.formData || {};
  return fd.Name || fd.name || fd['Full Name'] || fd.fullName || '';
};

const STATUS_STYLES = {
  EMPTY: 'bg-green-100 text-green-700 border-green-300',
  PARTIAL: 'bg-amber-100 text-amber-700 border-amber-300',
  FULL: 'bg-red-100 text-red-700 border-red-300',
};

const ConfirmDialog = ({ open, title, description, confirmLabel = 'Confirm', onConfirm, onCancel, loading }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <DialogContent className="max-w-md w-full p-6">
      <DialogHeader className="p-0 border-0">
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button onClick={onConfirm} disabled={loading} className="bg-red-600 hover:bg-red-700">
          {loading ? 'Freeing…' : confirmLabel}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

const LockerHistoryModal = ({ open, onClose, eventId, number, onViewUser }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [confirmUser, setConfirmUser] = useState(null);

  const { data: history = [], isLoading } = useQuery({
    queryKey: ['lockerNumberHistory', eventId, number],
    queryFn: () => ApiService.getLockerNumberHistory(eventId, number),
    enabled: open && !!eventId && number != null,
  });

  const freeMutation = useMutation({
    mutationFn: (userId) => ApiService.freeLockers(eventId, userId, [number]),
    onSuccess: (freed) => {
      showSuccess(`Freed locker(s): ${freed.join(', ')}`);
      queryClient.invalidateQueries(['lockerNumberHistory', eventId, number]);
      queryClient.invalidateQueries(['lockersDashboard', eventId]);
      queryClient.invalidateQueries(['lockerSummary', eventId]);
    },
    onError: (e) => showError(e.message || 'Failed to free locker'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl w-full p-4 sm:p-6 max-h-[85vh] overflow-y-auto">
        <DialogHeader className="p-0 border-0" showClose={false}>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle>Locker {number} — History</DialogTitle>
              <DialogDescription>Every assignment and release for this locker.</DialogDescription>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <div className="mt-4">
          {isLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400">No history yet for this locker.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-3">Registration ID</th>
                    <th className="py-2 pr-3">Assigned at</th>
                    <th className="py-2 pr-3">By</th>
                    <th className="py-2 pr-3">Freed at</th>
                    <th className="py-2 pr-3">By</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => {
                    const active = !h.freedAt;
                    return (
                      <tr key={`${h.userId}-${i}`} className="border-b last:border-0">
                        <td className="py-2 pr-3 font-medium">
                          {h.userId}
                          {h.userName && h.userName !== h.userId && (
                            <span className="block text-xs text-gray-400">{h.userName}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3">{formatDateTime(h.assignedAt)}</td>
                        <td className="py-2 pr-3 text-gray-600">{h.assignedBy || '—'}</td>
                        <td className="py-2 pr-3">
                          {h.freedAt ? formatDateTime(h.freedAt) : <span className="text-green-600">Active</span>}
                        </td>
                        <td className="py-2 pr-3 text-gray-600">{h.freedBy || '—'}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onViewUser?.(h.userId)}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" /> View
                            </Button>
                            {active && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={freeMutation.isPending}
                                onClick={() => setConfirmUser(h.userId)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Unlock className="w-3.5 h-3.5 mr-1" /> Free
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>

      <ConfirmDialog
        open={!!confirmUser}
        title={`Free locker ${number}?`}
        description={confirmUser ? `This releases locker ${number} from ${confirmUser}.` : ''}
        confirmLabel="Free locker"
        loading={freeMutation.isPending}
        onCancel={() => setConfirmUser(null)}
        onConfirm={() => freeMutation.mutate(confirmUser, { onSettled: () => setConfirmUser(null) })}
      />
    </Dialog>
  );
};

const DynamicLockersTable = ({ eventId }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [historyNumber, setHistoryNumber] = useState(null);
  const [manageUserId, setManageUserId] = useState(null);
  const [confirmFree, setConfirmFree] = useState(null); // { userId, number }

  const { data: summary } = useQuery({
    queryKey: ['lockerSummary', eventId],
    queryFn: () => ApiService.getLockerSummary(eventId),
    enabled: !!eventId,
  });

  const { data: response, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['lockersDashboard', eventId, page, appliedSearch, statusFilter],
    queryFn: () => ApiService.getLockersDashboard(eventId, {
      page,
      size: PAGE_SIZE,
      search: appliedSearch,
      status: statusFilter === 'ALL' ? '' : statusFilter,
    }),
    enabled: !!eventId,
    placeholderData: keepPreviousData,
  });

  const lockers = response?.content ?? [];
  const totalElements = response?.totalElements ?? 0;
  const totalPages = response?.totalPages ?? 0;

  // When a search returns no lockers, check whether the term is a real registration id so we can
  // offer to assign a locker to that user.
  const noResults = !isLoading && lockers.length === 0 && appliedSearch.trim().length > 0;
  const { data: searchedUser } = useQuery({
    queryKey: ['registrationLookup', eventId, appliedSearch],
    queryFn: () => ApiService.getRegistration(eventId, appliedSearch.trim()),
    enabled: !!eventId && noResults,
    retry: false,
  });

  const applySearch = () => {
    setPage(0);
    setAppliedSearch(searchTerm.trim());
  };

  const refreshDashboard = () => {
    queryClient.invalidateQueries(['lockersDashboard', eventId]);
    queryClient.invalidateQueries(['lockerSummary', eventId]);
  };

  const freeMutation = useMutation({
    mutationFn: ({ userId, number }) => ApiService.freeLockers(eventId, userId, [number]),
    onSuccess: (freed) => {
      showSuccess(`Freed locker(s): ${freed.join(', ')}`);
      refreshDashboard();
    },
    onError: (e) => showError(e.message || 'Failed to free locker'),
  });

  const autoAssignMutation = useMutation({
    mutationFn: async (userId) => {
      const avail = await ApiService.getAvailableLockers(eventId, { limit: 1 });
      const first = avail?.available?.[0];
      if (!first) throw new Error('No lockers available to assign');
      return ApiService.assignLockers(eventId, userId, [first]);
    },
    onSuccess: (assigned) => {
      showSuccess(`Assigned locker ${assigned.join(', ')}`);
      refreshDashboard();
    },
    onError: (e) => showError(e.message || 'Failed to auto-assign a locker'),
  });

  const metrics = [
    { label: 'Total', value: summary?.total, tone: 'text-gray-900' },
    { label: 'With free slots', value: summary?.available, tone: 'text-green-600' },
    { label: 'Full', value: summary?.assigned, tone: 'text-red-600' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-lg border p-3 text-center">
            <div className={`text-2xl font-semibold ${m.tone}`}>{m.value ?? '—'}</div>
            <div className="text-xs text-gray-500">{m.label}</div>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by locker # or registration ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={statusFilter}
                onValueChange={(v) => { setStatusFilter(v); setPage(0); }}
              >
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  <SelectItem value="EMPTY">Empty</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                  <SelectItem value="FULL">Full</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={applySearch} variant="outline">Search</Button>
              <Button onClick={() => refetch()} variant="outline" title="Refresh">
                <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Locker #</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Occupancy</th>
                  <th className="py-2 pr-3">Assigned to</th>
                  <th className="py-2 pr-3">Last updated</th>
                  <th className="py-2 pr-3 text-right">History</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="py-6 text-center text-gray-400">Loading…</td></tr>
                ) : lockers.length === 0 ? (
                  <tr><td colSpan={6} className="py-6 text-center text-gray-400">No lockers found.</td></tr>
                ) : (
                  lockers.map((l) => (
                    <tr key={l.number} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3 font-semibold">{l.number}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLES[l.status] || ''}`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className="inline-flex items-center gap-1 text-gray-700">
                          <Users className="w-3 h-3 text-gray-400" />
                          {l.occupancy}/{l.capacity}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        {l.occupants && l.occupants.length > 0 ? (
                          <div className="space-y-1">
                            {l.occupants.map((o) => (
                              <div key={o.userId} className="flex items-center flex-wrap gap-2">
                                <span
                                  title={o.name || o.userId}
                                  className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-mono"
                                >
                                  {o.userId}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setManageUserId(o.userId)}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={freeMutation.isPending}
                                  onClick={() => setConfirmFree({ userId: o.userId, number: l.number })}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Unlock className="w-3.5 h-3.5 mr-1" /> Free
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{formatDateTime(l.updatedAt)}</td>
                      <td className="py-2 pr-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => setHistoryNumber(l.number)}>
                          <History className="w-3 h-3 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* No locker for a searched registration → offer to assign one */}
          {noResults && searchedUser?.userId && (
            <div className="mt-4 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/50 p-4">
              <p className="text-sm text-gray-700">
                No locker assigned to <span className="font-mono font-semibold">{searchedUser.userId}</span>
                {displayNameOf(searchedUser) ? ` (${displayNameOf(searchedUser)})` : ''}.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => autoAssignMutation.mutate(searchedUser.userId)}
                  disabled={autoAssignMutation.isPending}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  {autoAssignMutation.isPending ? 'Assigning…' : 'Auto-assign first available'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setManageUserId(searchedUser.userId)}
                >
                  <Lock className="w-4 h-4 mr-1" />
                  Assign manually
                </Button>
              </div>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              {totalElements} locker(s){totalPages > 0 ? ` · page ${page + 1} of ${totalPages}` : ''}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <LockerHistoryModal
        open={historyNumber != null}
        onClose={() => setHistoryNumber(null)}
        eventId={eventId}
        number={historyNumber}
        onViewUser={(userId) => { setHistoryNumber(null); setManageUserId(userId); }}
      />

      {/* View registration + manage lockers for a single occupant */}
      <LockerAssignModal
        open={!!manageUserId}
        onClose={() => { setManageUserId(null); refreshDashboard(); }}
        eventId={eventId}
        userId={manageUserId}
      />

      <ConfirmDialog
        open={!!confirmFree}
        title={confirmFree ? `Free locker ${confirmFree.number}?` : ''}
        description={confirmFree ? `This releases locker ${confirmFree.number} from ${confirmFree.userId}.` : ''}
        confirmLabel="Free locker"
        loading={freeMutation.isPending}
        onCancel={() => setConfirmFree(null)}
        onConfirm={() => freeMutation.mutate(confirmFree, { onSettled: () => setConfirmFree(null) })}
      />
    </div>
  );
};

export default DynamicLockersTable;
